import React, { useState } from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useCopySku from "@/features/pathpick/hooks/useCopySku";
import PathPickMarginFormulaModal from "@/features/pathpick/components/product/PathPickMarginFormulaModal";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickAiImageBadge from "@/features/pathpick/components/product/PathPickAiImageBadge";
import PathPickProductImageZoomTooltip from "@/features/pathpick/components/product/PathPickProductImageZoomTooltip";
import MetricRow from "@/features/pathpick/components/product/PathPickProductCardMetricRow";
import PathPickProductCardComparison from "@/features/pathpick/components/product/PathPickProductCardComparison";
import PathPickProductCardRecommendation from "@/features/pathpick/components/product/PathPickProductCardRecommendation";
import {
  getDdayFromDateString,
  getPathPickProductBadgeState,
  getProductImageUrl,
  isProductImageAiCollected,
} from "@/features/pathpick/utils";
import {
  getPathPickMarketMetrics,
  getPathPickMarketRecommendation,
  PATH_PICK_MARKET_BASIS_LABEL_KEY,
} from "@/features/pathpick/utils/market";
import {
  PathPickMarket,
  PathPickMarketPriceBasis,
  PathPickProduct,
  PathPickProductCardLayout,
  PathPickProductCardViewSettings,
} from "@/features/pathpick/types";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import usePathPickProductOptionsHoverPrefetch from "@/features/pathpick/hooks/query/usePathPickProductOptionsHoverPrefetch";

interface Props {
  item: PathPickProduct;
  // 비교 기준 — 미지정 시 입찰가 기준
  marketPriceBasis?: PathPickMarketPriceBasis;
  isPicked?: boolean;
  isSelected?: boolean;
  // 품절 상품 — 카드를 dim 처리하고 이미지 위에 품절 배지를 노출한다.
  // (선택/상세 보기 등 기존 이벤트는 그대로 유지되고, 주문 차단은 화면별 actions가 맡는다)
  isSoldOut?: boolean;
  onToggleSelect?: () => void;
  onOpenSizeStock: () => void;
  onPick?: () => void;
  // 주문 수량 모달을 여는 핸들러 (미지정 시 주문 버튼은 동작하지 않는다)
  onOrder?: () => void;
  // 하단 버튼 row를 화면별로 교체하는 슬롯 (미지정 시 담기/주문 버튼)
  actions?: React.ReactNode;
  // "확인 필요" 배지 클릭 — 카드가 모달을 직접 들지 않고 목록 상위에 위임한다.
  // (카드마다 모달 인스턴스를 만들지 않기 위해서다)
  // 미지정이거나 열 요청이 없으면 배지는 클릭할 수 없는 표시로만 남는다.
  onOpenRequestDetail?: (params: { requestId: number; sku: string }) => void;
  // 카드 안 특정 영역만 파란 선으로 강조한다 (시작 가이드의 교육 단계 전용).
  // 미지정이면 아무것도 강조하지 않는다 — 운영 화면 3곳은 넘기지 않는다.
  highlightedRegion?: PathPickProductCardHighlight;
  // 상세 보기 hover 시 마켓 옵션 prefetch 를 끈다 (시작 가이드 전용).
  // 가이드는 목 상품만 다뤄서 실제 /options 를 부르면 안 된다.
  isOptionsPrefetchDisabled?: boolean;
  // 카드 보기 설정을 이 값으로 고정한다 (시작 가이드 전용).
  // 미지정이면 사용자 설정(localStorage)을 따른다 — 운영 화면 3곳은 넘기지 않는다.
  // 가이드는 튜토리얼이라 사용자가 비교 표시를 꺼 둔 상태에서도 같은 화면을 보여줘야 한다.
  viewOverride?: PathPickProductCardViewSettings;
}

// 강조 대상 — 시작 가이드의 상품 찾기 5단계가 하나씩 쓴다(비교 블록은 두 단계가 같이 쓴다).
// outline은 레이아웃을 밀지 않아서 강조가 켜져도 카드 크기가 그대로다.
export type PathPickProductCardHighlight =
  // 1/5 일반 vs 핫딜 — 썸네일(핫딜 배지가 얹히는 영역)
  | "thumbnail"
  // 2/5 카드 정보 — SKU·예상 공급가 묶음
  | "metrics"
  // 3/5 비교 기준 · 4/5 카드 보기 설정 — 두 마켓 블록과 추천 줄
  | "comparison"
  // 5/5 담기 — 기본 담기 버튼
  | "pick";

const HIGHLIGHT_STYLE = {
  outline: "2px solid #1272EF",
  outlineOffset: "3px",
  borderRadius: "4px",
} as const;

// 상품 카드 그리드 레이아웃 — 상품 찾기 카드뷰와 담은 상품 그리드가 공유한다.
//
// 나란히 보기는 카드 안을 두 열로 쪼개므로 배치에 따라 최소 폭과 최대 열 수를 달리 잡는다.
//   side           : 최소 240px, 최대 7열
//   stack / none   : 최소 200px, 최대 8열
//
// side 의 한 마켓 열이 쓰는 폭은 (카드 폭 − 40.5) / 2 다
// (카드 패딩 12×2 + 비교 블록 gap 8×2 + 구분선 0.5px). 값이 라벨 아래 줄로 내려가 둘이 폭을
// 나눠 쓰지 않으므로, 열에 필요한 것은 둘 중 더 긴 쪽뿐이다 — 가장 긴 문자열은 거래가 기준의
// POIZON 라벨 `30일 평균 거래가`(12px ≈ 92px)이고, 240px면 열이 99.8px라 접히지 않는다.
// 최대 열 수는 넓은 화면에서 카드가 무한정 넓어지지 않게 막는 상한이라 최소 폭과 함께 올린다
// (5로 두면 최소 폭을 줄여도 6열 이상으로 늘지 않는다).
export const getPathPickProductCardGrid = (
  layout: PathPickProductCardLayout
) => {
  const isSide = layout === "side";
  const minWidth = isSide ? "240px" : "200px";
  const columns = isSide ? 7 : 8;
  // 감산값은 gap 6px × (열 수 - 1) 이다.
  const gapTotal = 6 * (columns - 1);

  return {
    templateColumns: `repeat(auto-fill, minmax(max(${minWidth}, (100% - ${gapTotal}px) / ${columns}), 1fr))`,
    gap: "6px",
  } as const;
};

// 카드 프레임(여백·테두리·둥근 모서리) — 스켈레톤이 같은 값을 쓰도록 분리한다.
// 카드 프레임이 바뀌면 스켈레톤도 따라오므로 로딩 중 높이/폭이 어긋나지 않는다.
export const PATH_PICK_PRODUCT_CARD_FRAME = {
  flexDirection: "column",
  gap: "8px",
  padding: "12px",
  border: "0.5px solid #D4D4D1",
  borderRadius: "12px",
  background: "#FFFFFF",
  width: "100%",
} as const;

// pathpick 공용 상품 카드 (상품 찾기 카드뷰 / 담은 상품 그리드 / 시작 가이드 공용)
function PathPickProductCard({
  item,
  marketPriceBasis = "bidPrice",
  isPicked = false,
  isSelected = false,
  isSoldOut = false,
  onToggleSelect,
  onOpenSizeStock,
  onPick,
  onOrder,
  actions,
  onOpenRequestDetail,
  highlightedRegion,
  isOptionsPrefetchDisabled = false,
  viewOverride,
}: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const { copySku } = useCopySku();
  // 마켓별로 모달을 2개 만들지 않고, 어느 마켓의 산식을 여는지만 상태로 둔다.
  const [marginModalMarket, setMarginModalMarket] =
    useState<PathPickMarket | null>(null);
  const { layout: savedLayout } = usePathPickProductCardView();
  const layout: PathPickProductCardLayout = viewOverride
    ? viewOverride.showComparison
      ? viewOverride.comparisonLayout
      : "none"
    : savedLayout;

  // falsy(0/null/undefined) 값은 포맷하지 않고 undefined를 반환해 MetricRow가 "-"로 표시하게 한다.
  const formatKrw = (value?: number | null) =>
    value ? tCurrencyString(value, { type: "SYMBOL", code: "KRW" }) : undefined;

  // 두 마켓 지표는 비교 블록·추천 줄·산식 모달이 함께 쓰므로 한 번만 구한다.
  const metricsByMarket = {
    KREAM: getPathPickMarketMetrics(item, "KREAM", marketPriceBasis),
    POIZON: getPathPickMarketMetrics(item, "POIZON", marketPriceBasis),
  };
  const recommendation = getPathPickMarketRecommendation(
    metricsByMarket.KREAM,
    metricsByMarket.POIZON
  );
  const isComparisonVisible = layout !== "none";
  const comparisonLayout = layout === "side" ? "side" : "stack";

  const canToggle = Boolean(onToggleSelect) && !isPicked;

  const isHotDeal = item.productType === "LINE_SHEET_PRODUCT";
  const dday = item.deadline ? getDdayFromDateString(item.deadline) : null;
  // 마감 지남(음수)이거나 날짜가 없으면 D-day 배지를 숨긴다. 품절이면 주문할 수 없으므로
  // 마감 배지도 노출하지 않는다. D-3 이하면 붉은색.
  const showDday = isHotDeal && !isSoldOut && dday !== null && dday >= 0;
  const isDdayUrgent = dday !== null && dday <= 3;

  // 연동/네이버 엑셀/크림 엑셀 상태로 만든 배지 표시 조건 (완료·처리중·실패 중복 노출)
  const badgeState = getPathPickProductBadgeState(item);
  const hasStatusBadge =
    badgeState.isSyncCompleted ||
    badgeState.fileReadySuffixes.length > 0 ||
    badgeState.isProcessing ||
    badgeState.hasFailure;

  // 실패한 요청의 상세를 열 수 있는지 — 서버가 슬롯에 requestId를 주지 않는 옛 데이터도 있어
  // 열 대상이 확실할 때만 배지를 클릭 가능하게 만든다.
  const canOpenRequestDetail =
    badgeState.failedRequestId != null && Boolean(onOpenRequestDetail);

  // 실패 배지는 카드 선택 토글을 막고, 화면 이동 없이 그 자리에서 요청 상세 모달을 연다.
  // 어떤 상품을 확인하려는지 검색어(SKU)까지 함께 넘긴다.
  const handleOpenRequestDetail = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (badgeState.failedRequestId == null) {
      return;
    }
    onOpenRequestDetail?.({
      requestId: badgeState.failedRequestId,
      sku: item.sku,
    });
  };

  const handleOpenSizeStock = (event: React.MouseEvent) => {
    event.stopPropagation();
    onOpenSizeStock();
  };

  // 대표 이미지 — 확대 툴팁과 돋보기 노출 여부가 같은 값을 봐야 해서 한 번만 구한다.
  const productImageUrl = getProductImageUrl(item);

  // 모달이 열리고 나서 부르면 300~400ms 기다리게 된다. hover 에서 두 마켓을 함께 당긴다.
  // 비교를 감췄으면 상세 모달도 마켓 값을 그리지 않으므로 미리 당길 이유가 없다.
  const sizeStockHoverProps = usePathPickProductOptionsHoverPrefetch(
    item,
    isOptionsPrefetchDisabled || !isComparisonVisible
  );

  const marginModalMetrics = marginModalMarket
    ? metricsByMarket[marginModalMarket]
    : null;

  return (
    <>
      <Flex
        position={"relative"}
        // 아래 zIndex 1(품절 dim)·2(배지/체크박스/버튼)는 카드 안에서의 순서다.
        // 루트가 쌓임 맥락을 만들지 않으면 그 값들이 전역 순서로 새어나가
        // 목록 하단의 sticky 페이지네이션 같은 바깥 요소를 뚫고 올라온다.
        zIndex={0}
        {...PATH_PICK_PRODUCT_CARD_FRAME}
        {...(isSelected ? { border: "1px solid #1272EF" } : {})}
        cursor={canToggle ? "pointer" : "default"}
        onClick={() => canToggle && onToggleSelect?.()}
        _hover={{ boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.10)" }}
      >
        <Box
          position={"relative"}
          padding={"22px 6px"}
          {...(highlightedRegion === "thumbnail" ? HIGHLIGHT_STYLE : {})}
        >
          {/* 배지를 이미지 모서리에 맞추려고 이미지만 따로 감싼다.
              바깥 Box 에 붙이면 카드 좌상단 체크박스와 겹친다 — 이미지 기준이면
              이미지 위쪽 패딩(22px)만큼 내려와 체크박스 바로 아래에 놓인다. */}
          <Box position={"relative"}>
            <Image
              src={productImageUrl}
              fallbackSrc={"/icons-v1_1/null.png"}
              width={"100%"}
              aspectRatio={1}
              objectFit={"contain"}
              background={"#FFFFFF"}
              borderRadius={"8px"}
              alt={item.name}
            />
            {/* 자체 이미지가 없어 AI 수집 이미지를 대신 쓴 경우 표시한다. */}
            {isProductImageAiCollected(item) && <PathPickAiImageBadge />}
            {/* 돋보기 hover·클릭으로 확대 이미지를 띄운다 — 상세 모달을 열지 않고 상품을
                확인한다. 클릭하면 마우스를 떼도 확대 이미지가 고정된다.
                이미지가 없으면(null.png 폴백) 확대할 게 없어 아이콘도 그리지 않는다.
                이미지 좌상단은 AI 배지, 카드 좌상단은 체크박스라 우상단만 비어 있다. */}
            {productImageUrl && (
              <PathPickProductImageZoomTooltip
                imageUrl={productImageUrl}
                alt={item.name}
                isAiCollected={isProductImageAiCollected(item)}
                // 카드가 그리드로 촘촘히 깔려 있어 지나가는 마우스에는 뜨지 않게 한다.
                openDelay={300}
                isClickToPinEnabled={true}
              >
                <Flex
                  // 모바일은 확대 기능을 제공하지 않아 돋보기도 감춘다.
                  display={{ base: "none", md: "flex" }}
                  position={"absolute"}
                  top={"4px"}
                  right={"4px"}
                  zIndex={2}
                  alignItems={"center"}
                  justifyContent={"center"}
                  padding={"4px"}
                  borderRadius={"50%"}
                  background={"rgba(255, 255, 255, 0.9)"}
                  boxShadow={"0px 1px 2px 0px rgba(0, 0, 0, 0.15)"}
                  cursor={"zoom-in"}
                  // 카드 클릭은 선택을 토글하므로 아이콘 클릭은 카드로 넘기지 않는다.
                  onClick={(event) => event.stopPropagation()}
                >
                  <CustomIcon
                    src={"search-plus"}
                    width={16}
                    height={16}
                    color={"#444440"}
                  />
                </Flex>
              </PathPickProductImageZoomTooltip>
            )}
          </Box>
          {/* 품절 배지 — 이미지 중앙. dim 레이어보다 위에 오도록 여기서 그린다. */}
          {isSoldOut && (
            <Flex
              position={"absolute"}
              top={"0"}
              left={"0"}
              right={"0"}
              bottom={"0"}
              alignItems={"center"}
              justifyContent={"center"}
              pointerEvents={"none"}
              zIndex={2}
            >
              <Text fontSize={"20px"} fontWeight={"bold"} color={"#1D1D1B"}>
                {t("path_pick:basket.card.sold_out")}
              </Text>
            </Flex>
          )}
          {/* 체크박스는 카드 클릭으로 토글되므로 onChange 없이 표시만 담당한다. */}
          {onToggleSelect && (
            <Box position={"absolute"} top={"0"} left={"0"} zIndex={2}>
              <AppCheckBox
                size={"small"}
                isChecked={isSelected}
                isDisabled={isPicked}
              />
            </Box>
          )}
          {isHotDeal && (
            <Flex position={"absolute"} top={"0"} right={"0"} gap={"4px"}>
              <PathPickBadge
                label={t(
                  "path_pick:search_products.product_list.card.hot_deal"
                )}
                colorVariant={"yellow"}
              />
              {showDday && (
                <PathPickBadge
                  label={dday === 0 ? "D-DAY" : `D-${dday}`}
                  colorVariant={isDdayUrgent ? "red" : "blue"}
                />
              )}
            </Flex>
          )}
          {hasStatusBadge && (
            <Flex
              position={"absolute"}
              bottom={"4px"}
              right={"4px"}
              gap={"4px"}
              justifyContent={"flex-end"}
              flexWrap={"wrap-reverse"}
            >
              {badgeState.isSyncCompleted && (
                <PathPickBadge
                  label={t(
                    "path_pick:search_products.product_list.card.badge_api_sync_complete"
                  )}
                  colorVariant={"green"}
                />
              )}
              {badgeState.fileReadySuffixes.length > 0 && (
                <PathPickBadge
                  label={t(
                    "path_pick:search_products.product_list.card.badge_file_ready"
                  )}
                  colorVariant={"green"}
                  suffixes={badgeState.fileReadySuffixes}
                />
              )}
              {badgeState.isProcessing && (
                <PathPickBadge
                  label={t(
                    "path_pick:search_products.product_list.card.badge_processing"
                  )}
                  colorVariant={"blue"}
                  leadingIcon={"clock"}
                />
              )}
              {badgeState.hasFailure && (
                <PathPickBadge
                  label={t(
                    "path_pick:search_products.product_list.card.badge_check_required"
                  )}
                  colorVariant={"red"}
                  leadingIcon={"warning-circle-solid"}
                  {...(canOpenRequestDetail && {
                    trailingIcon: "chevron-right",
                    onClick: handleOpenRequestDetail,
                  })}
                />
              )}
            </Flex>
          )}
        </Box>

        {/* 브랜드/상품명: 클릭 시 상품 상세 정보 모달 */}
        <Flex
          flexDirection={"column"}
          gap={"2px"}
          cursor={"pointer"}
          onClick={handleOpenSizeStock}
          {...sizeStockHoverProps}
        >
          <Text
            textStyle={"body-lg-bold"}
            color={"#1D1D1B"}
            noOfLines={1}
            wordBreak={"break-all"}
          >
            {item.brandName ?? "-"}
          </Text>
          <Text
            textStyle={"body-sm-medium"}
            color={"#8F8F8C"}
            noOfLines={1}
            wordBreak={"break-all"}
          >
            {item.name ?? "-"}
          </Text>
        </Flex>

        <Flex
          flexDirection={"column"}
          gap={"4px"}
          {...(highlightedRegion === "metrics" ? HIGHLIGHT_STYLE : {})}
        >
          {/* SKU 는 라벨/값 두 칸이 아니라 한 줄로 보이게 두되, 눌러서 복사되는 것은 값뿐이다
              — 라벨까지 함께 반응하면 "SKU"라는 글자도 누를 수 있는 것처럼 보인다.
              (카드 클릭은 선택을 토글하므로 stopPropagation 한다) */}
          <Flex gap={"4px"} alignItems={"center"} maxWidth={"100%"}>
            <Text
              textStyle={"caption-medium"}
              color={"#8F8F8C"}
              flexShrink={0}
            >
              {t("path_pick:search_products.product_list.card.sku")}
            </Text>
            <Text
              textStyle={"caption-medium"}
              color={"#8F8F8C"}
              noOfLines={1}
              wordBreak={"break-all"}
              cursor={"pointer"}
              _hover={{ color: "#1272EF" }}
              onClick={(event) => {
                event.stopPropagation();
                copySku(item.sku);
              }}
            >
              {item.sku}
            </Text>
          </Flex>

          {/* 상품 상세 정보 — 비교 정보보다 위에 둔다(비교 표시를 꺼도 자리가 그대로다).
              위아래 지표 행이 좌측 라벨 축에 붙어 있어, 이 행은 우측 끝으로 빼야
              같은 축에 겹치지 않고 누를 수 있는 것으로 먼저 읽힌다.
              색은 명도가 아니라 색조로 가른다 — 바로 아래 공급가 값(#444440)과 명도를
              비슷하게 둬야 카드가 여러 장 깔려도 시끄럽지 않고, 파란 색조만으로 구분된다. */}
          <Flex
            gap={"4px"}
            alignItems={"center"}
            cursor={"pointer"}
            alignSelf={"flex-end"}
            onClick={handleOpenSizeStock}
            {...sizeStockHoverProps}
            color={"#005AAD"}
            _hover={{ color: "#1272EF" }}
          >
            <Text textStyle={"body-sm-medium"} fontWeight={600}>
              {t(
                "path_pick:search_products.product_list.card.view_option_details"
              )}
            </Text>
            <CustomIcon src={"chevron-right"} width={16} height={16} />
          </Flex>

          <MetricRow
            label={t(
              "path_pick:search_products.product_list.card.expected_supply_price"
            )}
            value={formatKrw(item.salePrice.value)}
          />
        </Flex>

        {isComparisonVisible && (
          <Flex
            flexDirection={"column"}
            gap={"8px"}
            {...(highlightedRegion === "comparison" ? HIGHLIGHT_STYLE : {})}
          >
            <PathPickProductCardComparison
              item={item}
              kreamMetrics={metricsByMarket.KREAM}
              poizonMetrics={metricsByMarket.POIZON}
              basis={marketPriceBasis}
              layout={comparisonLayout}
              onOpenMarginFormula={setMarginModalMarket}
            />
            {/* 한쪽 마켓만 매칭된 상품은 비교 대상이 없어 추천 줄을 그리지 않는다. */}
            {recommendation && (
              <PathPickProductCardRecommendation
                recommendation={recommendation}
                layout={comparisonLayout}
              />
            )}
          </Flex>
        )}

        <Flex
          gap={"4px"}
          // 카드 높이가 서로 달라도 버튼 줄은 바닥에 맞춘다.
          marginTop={"auto"}
          // 품절 dim 위로 올린다 — 버튼은 흐려지면 안 된다.
          position={"relative"}
          zIndex={2}
          onClick={(event) => event.stopPropagation()}
        >
          {actions ?? (
            <>
              <AppButton
                variant={"primary"}
                width={"100%"}
                isDisabled={isPicked}
                onClick={onPick}
                chakraProps={{
                  px: "8px",
                  ...(highlightedRegion === "pick" ? HIGHLIGHT_STYLE : {}),
                }}
              >
                <Flex
                  gap={"4px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <CustomIcon src={"package"} width={16} height={16} />
                  <Text textStyle={"body-sm-bold"}>
                    {isPicked
                      ? t("path_pick:search_products.product_list.picked")
                      : t("path_pick:search_products.product_list.pick")}
                  </Text>
                </Flex>
              </AppButton>
              <AppButton
                variant={"white"}
                width={"80%"}
                onClick={onOrder}
                chakraProps={{ px: "8px" }}
              >
                <Flex
                  gap={"4px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <CustomIcon src={"cart"} width={16} height={16} />
                  <Text textStyle={"body-sm-bold"}>
                    {t("path_pick:search_products.product_list.order")}
                  </Text>
                </Flex>
              </AppButton>
            </>
          )}
        </Flex>

        {/* 품절 dim — pointerEvents none이라 카드 선택/상세 보기 클릭은 그대로 통과한다. */}
        {isSoldOut && (
          <Box
            position={"absolute"}
            top={"0"}
            left={"0"}
            right={"0"}
            bottom={"0"}
            borderRadius={"12px"}
            background={"rgba(252, 252, 251, 0.6)"}
            pointerEvents={"none"}
            zIndex={1}
          />
        )}
      </Flex>
      {marginModalMarket && marginModalMetrics && (
        <PathPickMarginFormulaModal
          isOpen
          onClose={() => setMarginModalMarket(null)}
          market={marginModalMarket}
          item={item}
          supplyPrice={item.salePrice.value}
          basisValue={marginModalMetrics.basisValue}
          basisLabel={t(
            PATH_PICK_MARKET_BASIS_LABEL_KEY[marginModalMarket][
              marketPriceBasis
            ]
          )}
          expectedMargin={marginModalMetrics.expectedMargin}
          marginRate={marginModalMetrics.marginRate}
        />
      )}
    </>
  );
}

export default PathPickProductCard;
